/**
 * AG GRID COMMUNITY 36 — Theming API only. The legacy CSS-file themes are not imported, no
 * `ag-theme-*` stylesheet exists and no hand-written `.ag-` override is allowed anywhere. Every
 * param reads a CSS variable Phase 1 defined, so the grid follows the theme toggle with no
 * JavaScript: NEVER a raw hex here.
 *
 * THE TARGET LOOK (`SPEC-APP.md` §3.4): a headless table. A header row, one hairline under it,
 * rows. AG Grid is the engine, never the skin — so the wrapper border, the wrapper radius, the
 * column separators, the header fill and the zebra striping are all off in the params below, and
 * the resize handle, the drag-to-move, the header menu/filter buttons and the floating-filter row
 * are all off in `RegisterGrid`'s `defaultColDef`.
 *
 * Only the Community modules this wrapper actually uses are registered.
 */
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  ModuleRegistry,
  NumberEditorModule,
  QuickFilterModule,
  RowApiModule,
  RowSelectionModule,
  RowStyleModule,
  ValidationModule,
  themeQuartz,
} from "ag-grid-community";

let registered = false;

/** Registered once, lazily, so a page that never mounts a grid pays nothing. */
export function registerGridModules(): void {
  if (registered) return;
  registered = true;
  ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    /* NO filter module: AG Grid column filters are off on both doctrines. The register filters
       through the toolbar band and the quick filter, which is the QuickFilterModule below. */
    /* The one inline-editable grid (priority policies) edits numbers only. */
    NumberEditorModule,
    QuickFilterModule,
    RowApiModule,
    RowSelectionModule,
    ColumnAutoSizeModule,
    CellStyleModule,
    RowStyleModule,
    ...(import.meta.env.DEV ? [ValidationModule] : []),
  ]);
}

/**
 * Density first: rows on screen are the optimisation target.
 *
 * `rowHeight` and `headerHeight` read CSS variables and never literals — group 10 raises the
 * control scale on tablet by redefining `--row-height` for a subtree, and a literal here would
 * defeat that.
 */
export const registerGridTheme = themeQuartz.withParams({
  /* Surface: the grid sits directly on the page. No wrapper border, no radius, no card. */
  wrapperBorder: false,
  wrapperBorderRadius: 0,
  backgroundColor: "var(--color-background)",
  foregroundColor: "var(--color-foreground)",
  borderColor: "var(--grid-border)",

  /* Header: text on the page background, one hairline beneath, no vertical separators.
     `headerBackgroundColor` does not exist in v36 — the header fill is `chromeBackgroundColor`. */
  chromeBackgroundColor: "transparent",
  headerTextColor: "var(--color-muted-foreground)",
  headerFontSize: "var(--type-caption-size)",
  headerVerticalPaddingScale: 0.7,
  headerFontWeight: 600,
  headerHeight: "var(--control-height-md)",
  headerRowBorder: { style: "solid", width: 1, color: "var(--grid-border)" },
  headerColumnBorder: false,
  columnBorder: false,
  /* No resize handle exists (`defaultColDef`), and its 2px divider line is not a separator we
     keep either. */
  headerColumnResizeHandleColor: "transparent",
  /* A pinned column's edge is a vertical separator too — off, like every other one. */
  pinnedColumnBorder: false,
  pinnedRowBorder: false,

  /* Rows: no zebra. A hover wash and a selected wash carry the state instead. */
  oddRowBackgroundColor: "transparent",
  rowBorder: { style: "solid", width: 1, color: "var(--grid-border)" },
  rowHoverColor: "var(--grid-row-hover)",
  selectedRowBackgroundColor: "var(--grid-row-selected)",
  rowHeight: "var(--row-height)",

  /* Type and metrics. */
  fontFamily: "var(--font-sans)",
  cellFontFamily: "var(--font-sans)",
  fontSize: "var(--type-table-size)",
  spacing: "var(--space-sm)",
  cellHorizontalPadding: "var(--space-sm)",
  borderRadius: "var(--radius)",

  /* The focus ring exists for the ONE editable grid; every other grid suppresses cell focus. */
  focusShadow: { color: "var(--color-ring)", spread: 2 },
  accentColor: "var(--color-primary)",
  inputBackgroundColor: "var(--color-background)",
  inputBorder: { color: "var(--color-input)" },
});

/**
 * THE PLAIN TABLES READ THE GRID'S OWN NUMBERS. `IssuesTable` and `OutboundQuantitiesTable` are
 * not AG Grid, but they sit on the same pages and must read as the same table, so they take the
 * hairline colour, the header weight, the row height and the cell padding from the very CSS
 * variables the theme above reads. Never a second set of numbers.
 */
export const plainTable = {
  /** No wrapper border, no card: the table sits on the page like the grid does. */
  root: "w-full border-separate border-spacing-0",
  /** Sticky header row with ONE hairline beneath it. */
  head: "type-caption uppercase tracking-wide text-muted-foreground sticky top-0 z-10 h-[var(--control-height-md)] border-b border-[var(--grid-border)] bg-[var(--color-background)] px-[var(--space-sm)] text-left align-middle font-semibold",
  /** A hairline between rows, and the same hover wash. */
  row: "border-0 hover:bg-[var(--grid-row-hover)]",
  cell: "type-table h-[var(--row-height)] border-b border-[var(--grid-border)] px-[var(--space-sm)] py-0 align-middle",
  /** Identifier, numeric and date cells. */
  numeric: "tabular-nums",
} as const;
